import { SdkContext } from '../internal'
import { listItems } from '@/lib/utils'
import type { DayPassCheckInEntry } from '@/types/dayPasses'
import type {
  AttendanceKiosk,
  AttendanceMySummary,
  AttendanceOverview,
  AttendancePage,
  AttendancePresent,
  AttendanceVisit,
  AttendanceVisitsParams,
  GuestCheckInInput
} from '@/types/attendance'

const toPage = <T>(result: unknown): AttendancePage<T> => {
  const page = result as { pagination?: AttendancePage<T>['pagination'] } | null
  return { items: listItems<T>(result), pagination: page?.pagination ?? null }
}

/** A paginated answer whose `data` is an object: the transport may hand it back as `items`, `data` or flat. */
type RawPaged<T> = T & { items?: T; data?: T; pagination?: AttendancePage<AttendanceVisit>['pagination'] } | null


/**
 * F-78 phase 1A: attendance. Paths are written by hand (F-03). The server also sends the
 * community's `timeZone` next to the list, but the transport unwraps the envelope and drops it:
 * screens read the zone from the community (the same IANA name).
 */
export class AttendanceResource {
  constructor(private ctx: SdkContext) {}

  async overview(spaceId: string): Promise<AttendanceOverview> {
    return this.ctx.call({ httpMethod: 'GET', path: '/attendance/spaces/{spaceId}/overview' }, { spaceId })
  }

  /** No body: the caller marks themself. With `memberId`, the team marks a member. */
  /** F-249: on a space that charges passes the answer carries `entry`; no plan and no pass is a 409 `noDayPass`. */
  async checkIn(spaceId: string, memberId?: string): Promise<{ visit: AttendanceVisit; alreadyCheckedIn: boolean; entry?: DayPassCheckInEntry }> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/attendance/spaces/{spaceId}/check-in', hasBody: true },
      { spaceId, body: memberId ? { memberId } : {} }
    )
  }

  /** No body: the caller closes their own visit. The team closes one by `visitId` or `memberId`. */
  async checkOut(spaceId: string, target: { visitId?: string; memberId?: string } = {}): Promise<{ visit: AttendanceVisit }> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/attendance/spaces/{spaceId}/check-out', hasBody: true },
      { spaceId, body: target }
    )
  }

  async present(spaceId: string, page = 1, limit = 50): Promise<AttendancePresent> {
    const raw = await this.ctx.raw<RawPaged<{ visits?: AttendanceVisit[]; occupancy?: AttendancePresent['occupancy'] }>>(
      { httpMethod: 'GET', path: '/attendance/spaces/{spaceId}/present' },
      { spaceId, query: { page, limit } }
    )
    /* Paginated: the transport hands back `{ items: { occupancy, visits }, pagination }`. */
    const inner = raw?.items ?? raw?.data ?? raw
    return {
      items: Array.isArray(inner?.visits) ? inner.visits : [],
      occupancy: inner?.occupancy ?? { count: 0, capacity: null },
      pagination: raw?.pagination ?? null
    }
  }

  async listVisits(spaceId: string, params: AttendanceVisitsParams = {}): Promise<AttendancePage<AttendanceVisit>> {
    const { kind, from, to, open, page, limit } = params
    return toPage(
      await this.ctx.raw(
        { httpMethod: 'GET', path: '/attendance/spaces/{spaceId}/visits' },
        { spaceId, query: { kind, from, to, open: open === undefined ? undefined : String(open), page, limit } }
      )
    )
  }

  async myVisits(spaceId: string, page = 1, limit = 20): Promise<AttendancePage<AttendanceVisit> & { summary: AttendanceMySummary | null }> {
    const raw = await this.ctx.raw<RawPaged<{ visits?: AttendanceVisit[]; summary?: AttendanceMySummary | null }>>(
      { httpMethod: 'GET', path: '/attendance/me/visits' },
      { query: { spaceId, page, limit } }
    )
    const inner = raw?.items ?? raw?.data ?? raw
    return {
      items: Array.isArray(inner?.visits) ? inner.visits : [],
      summary: inner?.summary ?? null,
      pagination: raw?.pagination ?? null
    }
  }

  async updateVisit(visitId: string, body: { checkInAt?: string; checkOutAt?: string }): Promise<AttendanceVisit> {
    return this.ctx.call({ httpMethod: 'PATCH', path: '/attendance/visits/{visitId}', hasBody: true }, { visitId, body })
  }

  async deleteVisit(visitId: string): Promise<{ id: string }> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/attendance/visits/{visitId}' }, { visitId })
  }

  /** The team registers a guest (no account); idempotent by name + host. */
  async guestCheckIn(spaceId: string, body: GuestCheckInInput): Promise<{ visit: AttendanceVisit; alreadyCheckedIn: boolean }> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/attendance/spaces/{spaceId}/guest-check-in', hasBody: true },
      { spaceId, body }
    )
  }

  async listKiosks(spaceId: string): Promise<AttendanceKiosk[]> {
    const result = await this.ctx.call<{ kiosks?: AttendanceKiosk[] }>({ httpMethod: 'GET', path: '/attendance/spaces/{spaceId}/kiosks' }, { spaceId })
    return result?.kiosks ?? []
  }

  /** Returns the URL once: it is never shown again. */
  async createKiosk(spaceId: string, name: string): Promise<{ kiosk: AttendanceKiosk; url: string }> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/attendance/spaces/{spaceId}/kiosks', hasBody: true },
      { spaceId, body: { name } }
    )
  }

  async revokeKiosk(spaceId: string, kioskId: string): Promise<{ kiosk: AttendanceKiosk }> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/attendance/spaces/{spaceId}/kiosks/{kioskId}' }, { spaceId, kioskId })
  }
}
